import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  useContext,
} from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import { Axis } from '@prisjakt/ui';
import { Hideable } from '@prisjakt/ui/hideable';
import { Panel } from '@prisjakt/ui/panel';
import { ScrollLink, ScrollElement } from 'src/components/scroll';
import { ProductsSlice } from '../../../../types';
import CategoryInformation from '../../../category/components/category-information';
import { LayoutMode } from '../../../../components/layout-buttons/types';
import { ICON_SIZE } from '../../../../components/layout-buttons/constants';
import Pagination from './pagination';
import SearchInput, { useSearchQuery } from './search-input';
import LayoutButtons from '../../../../components/layout-buttons';
import { ProductsHeader } from '../../../../components/products-header';
import formatNumber from '../../../../globals/helpers/format-number';
import OverlaySort from './overlay-sort';
import { ToggleButton, VariantEnum } from '../toggle-button';
import SortOptions from './sort-options';
import { NoSearchResults } from 'src/components/no-search-results';
import * as S from './styles';
import { TooltipPlacement } from 'src/components/tooltip-new';
import { IconMagnifier, IconSort, IconFilter } from '@prisjakt/ui/icon';
import { FilterContext } from '../../filter-context';
import { DesktopOnly, MediumDown, MobileOnly } from 'src/ui/block';
import { useViewportContext } from 'src/components/viewport/context';

type Props = Pick<
  ProductsSlice,
  | 'id'
  | 'pageInfo'
  | 'productsCount'
  | 'storeCount'
  | 'legalText'
  | 'availableSorts'
  | 'currentSort'
  | 'maxHitsAmount'
> & {
  children: ReactNode;
  layout: LayoutMode;
  onLayoutChange: (layout: LayoutMode) => void;
  compareProductsToggle?: ReactNode;
  availableSortsTranslations: Record<string, string>;
  supportedViewModes: ReadonlyArray<LayoutMode>;
};

const ProductOffersContainer = ({
  pageInfo,
  productsCount,
  availableSorts,
  availableSortsTranslations,
  currentSort,
  onLayoutChange,
  layout,
  storeCount,
  legalText,
  children,
  maxHitsAmount,
  compareProductsToggle,
  supportedViewModes,
}: Props) => {
  const { market, t } = useAppContext();
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const searchInputRef: React.RefObject<HTMLInputElement> = createRef();
  const [query] = useSearchQuery();
  const [showSearch, setShowSearch] = useState(query !== '');

  useEffect(() => setShowSearch(query !== '' || showSearch), [
    query,
    showSearch,
  ]);

  const onClickSearch = useCallback(() => {
    const visible = query !== '' || !showSearch;
    setShowSearch(visible);
    if (visible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [setShowSearch, showSearch, query, searchInputRef]);

  const { isMobile, isTablet } = useViewportContext();
  const removeHeaderRowMarginBottom = isMobile || isTablet;

  const headerElement = useMemo(
    () => (
      <ProductsHeader
        dataBotify="ProductCollectionProductCount"
        displayOptions={
          <>
            <DesktopOnly>{compareProductsToggle}</DesktopOnly>

            <DesktopOnly>
              <SearchInput />
            </DesktopOnly>

            <MediumDown>
              <S.SearchInputIcon
                data-test="SearchInputIcon"
                onClick={onClickSearch}
                iconSize={ICON_SIZE}
              >
                <S.Tooltip
                  placement={TooltipPlacement.LEFT}
                  title={t('category', 'product-search.placeholder')}
                >
                  <IconMagnifier />
                </S.Tooltip>
              </S.SearchInputIcon>
            </MediumDown>

            <LayoutButtons
              layoutModes={supportedViewModes}
              activeLayout={layout}
              setLayout={onLayoutChange}
            />
          </>
        }
        sortOptions={
          <DesktopOnly>
            <SortOptions
              currentSort={currentSort}
              availableSorts={availableSorts}
              availableSortsTranslations={availableSortsTranslations}
            />
          </DesktopOnly>
        }
        title={t('category', 'products.count', {
          n: productsCount,
          count: formatNumber(productsCount, market.code),
        })}
        noMarginBottom={removeHeaderRowMarginBottom}
      >
        <MediumDown>
          <Hideable collapse={Axis.Y} visible={showSearch} renderWhenHidden>
            <SearchInput
              alwaysOpen
              inputWidth="100%"
              inputRef={searchInputRef}
            />
          </Hideable>
        </MediumDown>
      </ProductsHeader>
    ),
    [
      availableSorts,
      availableSortsTranslations,
      compareProductsToggle,
      currentSort,
      layout,
      market.code,
      onClickSearch,
      onLayoutChange,
      productsCount,
      removeHeaderRowMarginBottom,
      searchInputRef,
      showSearch,
      supportedViewModes,
      t,
    ]
  );

  const footerElement = useMemo(
    () => (
      <ScrollLink to="Products">
        <Pagination
          pageInfo={pageInfo}
          total={Math.min(productsCount, maxHitsAmount)}
        />
      </ScrollLink>
    ),
    [pageInfo, productsCount, maxHitsAmount]
  );

  const toggleMobileSort = useCallback(() => {
    setMobileSortOpen(!mobileSortOpen);
  }, [mobileSortOpen]);

  const onClose = useCallback(() => {
    setMobileSortOpen(false);
  }, []);

  const showTooManyHitsNotice =
    productsCount > maxHitsAmount &&
    (pageInfo.offset || 0) + (pageInfo.limit || 0) >= maxHitsAmount;

  const { mobileFilterOpen, setMobileFilterOpen, count } = useContext(
    FilterContext
  );

  return (
    <ScrollElement name="Products">
      <S.Wrapper>
        {mobileSortOpen && (
          <MobileOnly>
            <div>
              <OverlaySort
                currentSort={currentSort}
                onClose={onClose}
                sortOptions={availableSorts}
                sortTranslations={availableSortsTranslations}
              />
            </div>
          </MobileOnly>
        )}

        <Panel>
          {headerElement}

          {productsCount === 0 && <NoSearchResults />}
          {productsCount > 0 && (
            <MediumDown>
              <S.MobileWrapper>
                {/* filter */}
                <S.StyledFilterButton
                  count={count}
                  icon={<IconFilter size={18} />}
                  onToggle={() => setMobileFilterOpen(!mobileFilterOpen)}
                  title={t('filter', 'filter.title')}
                />

                <S.CompareSortWrapper>
                  {/* compare */}
                  {compareProductsToggle}

                  {/* sort */}
                  <ToggleButton
                    variant={VariantEnum.OUTLINED}
                    icon={<IconSort size={24} />}
                    onToggle={toggleMobileSort}
                    truncate
                    data-test="SortOrderButton"
                  />
                </S.CompareSortWrapper>
              </S.MobileWrapper>
            </MediumDown>
          )}
          {children}
          {showTooManyHitsNotice && (
            <S.Notice>
              {t('category', 'product-collection.too-many-hits-notice', {
                maxHitsAmount,
              })}
            </S.Notice>
          )}
          {footerElement}
          {legalText && (
            <CategoryInformation
              statistics={
                typeof storeCount === 'number'
                  ? {
                      productsCount,
                      storesCount: storeCount,
                    }
                  : undefined
              }
            />
          )}
        </Panel>
      </S.Wrapper>
    </ScrollElement>
  );
};

export default ProductOffersContainer;
